import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { Menu, MenuItem, Tooltip } from '@mui/material';

/**
 * ExportDropdownMenu Component
 * 
 * A reusable dropdown menu for export functionality with 3 options:
 * 1. Download All - Export all records (no filters, no pagination)
 * 2. Download with Filters - Export filtered records (no pagination)
 * 3. Download Current View - Export current page (filters + pagination)
 * 
 * @param {Function} onExportAll - Callback when "Download All" is clicked
 * @param {Function} onExportWithFilters - Callback when "Download with Filters" is clicked
 * @param {Function} onExportCurrentView - Callback when "Download Current View" is clicked
 * @param {boolean} hasFilters - Whether filters are currently applied (to disable/enable options)
 * @param {string} tooltipText - Custom tooltip text (default: "Download")
 * @param {string} className - Additional CSS classes for the button
 */
const ExportDropdownMenu = ({
  onExportAll,
  onExportWithFilters,
  onExportCurrentView,
  hasFilters = false,
  tooltipText = "Download",
  className = "text-gx-navy hover:text-gx-teal"
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExportAll = () => {
    handleClose();
    onExportAll?.();
  };

  const handleExportWithFilters = () => {
    handleClose();
    onExportWithFilters?.();
  };

  const handleExportCurrentView = () => {
    handleClose();
    onExportCurrentView?.();
  };

  return (
    <>
      <Tooltip title={tooltipText} arrow>
        <button
          type="button"
          className={className}
          onClick={handleClick}
          aria-label="Export options"
          aria-controls={open ? 'export-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <FontAwesomeIcon icon={faDownload} fontSize="22px" />
        </button>
      </Tooltip>

      <Menu
        id="export-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'export-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleExportAll}>
          <div className="flex flex-col">
            <span className="font-semibold text-sm">Download All</span>
            <span className="text-xs text-gray-500">
              Export all records (no filters, no pagination)
            </span>
          </div>
        </MenuItem>

        <MenuItem onClick={handleExportWithFilters} disabled={!hasFilters}>
          <div className="flex flex-col">
            <span className="font-semibold text-sm">Download with Filters</span>
            <span className="text-xs text-gray-500">
              {hasFilters 
                ? "Export filtered records (no pagination)" 
                : "Apply filters to enable this option"
              }
            </span>
          </div>
        </MenuItem>

        <MenuItem onClick={handleExportCurrentView}>
          <div className="flex flex-col">
            <span className="font-semibold text-sm">Download Current View</span>
            <span className="text-xs text-gray-500">
              Export current page (filters + pagination)
            </span>
          </div>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ExportDropdownMenu;
